import { useState, useEffect } from 'react';
import { P, cardStyle, inputStyle, labelStyle } from '../styles/theme';
import Btn from './Shared/Btn';
import { fetchTimeBlocks, insertManualBooking, deleteBooking } from '../services/bookingService';
import { today } from '../utils/formatters';

const DURATION_OPTIONS = [30, 60, 90, 120, 150, 180, 240, 300, 360, 480];

const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const h = Math.floor(i / 2);
  const m = i % 2 === 0 ? '00' : '30';
  return `${String(h).padStart(2, '0')}:${m}`;
});

export default function TimeBlocks() {
  const [blocks,  setBlocks]  = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [form, setForm] = useState({ date: today(), time: '', duration: '60', label: '' });

  const load = async () => {
    setLoading(true);
    setBlocks(await fetchTimeBlocks());
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async () => {
    if (!form.date || !form.time) return alert('Date and start time are required.');
    const ts = new Date(`${form.date}T${form.time}:00`).toISOString();
    const label = form.label.trim() || 'Time Block';
    setSaving(true);
    try {
      await insertManualBooking({
        client_name:    label,
        client_phone:   '',
        client_email:   '',
        service_name:   label,
        booking_date:   ts,
        total_amount:   0,
        deposit_amount: 0,
        balance_due:    0,
        status:         'blocked',
        has_allergy:    false,
        total_duration: parseInt(form.duration) || 60,
      });
      setForm({ date: today(), time: '', duration: '60', label: '' });
      await load();
    } catch (err) { alert('Could not add block: ' + err.message); }
    setSaving(false);
  };

  const handleRemove = async (id) => {
    if (!window.confirm('Remove this time block?')) return;
    try { await deleteBooking(id); await load(); }
    catch (err) { alert('Could not remove: ' + err.message); }
  };

  return (
    <div>
      <div style={{ marginBottom: '20px' }}>
        <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '26px', color: P.deep, fontWeight: 600 }}>🚫 Time Blocks</h2>
        <p style={{ fontSize: '13px', color: P.muted, marginTop: '4px' }}>
          클라이언트 앱에서 예약 불가 시간대를 직접 설정합니다. 설정한 시간은 즉시 반영됩니다.
        </p>
      </div>

      {/* Add form */}
      <div style={{ ...cardStyle, border: `2px solid ${P.rose}55`, marginBottom: '24px' }}>
        <h4 style={{ color: P.deep, marginBottom: '14px', fontWeight: 700 }}>+ New Time Block</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
          <div>
            <label style={labelStyle}>Date *</label>
            <input type="date" style={inputStyle} value={form.date}
              onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
          </div>
          <div>
            <label style={labelStyle}>Start Time *</label>
            <select style={inputStyle} value={form.time}
              onChange={e => setForm(f => ({ ...f, time: e.target.value }))}>
              <option value="">-- select --</option>
              {TIME_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '14px' }}>
          <div>
            <label style={labelStyle}>Duration</label>
            <select style={inputStyle} value={form.duration}
              onChange={e => setForm(f => ({ ...f, duration: e.target.value }))}>
              {DURATION_OPTIONS.map(d => (
                <option key={d} value={d}>
                  {d < 60 ? `${d} min` : `${d / 60}h${d % 60 ? ` ${d % 60}m` : ''}`}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Label (optional)</label>
            <input style={inputStyle} placeholder="e.g. Lunch break"
              value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))} />
          </div>
        </div>
        <Btn onClick={handleAdd} disabled={saving}>
          {saving ? 'Saving...' : '+ Add Block'}
        </Btn>
      </div>

      {/* Block list */}
      <h4 style={{ color: P.deep, fontWeight: 700, marginBottom: '12px', fontSize: '14px' }}>
        Active Blocks ({blocks.length})
      </h4>
      {loading ? (
        <p style={{ color: P.muted, textAlign: 'center', padding: '30px' }}>Loading...</p>
      ) : blocks.length === 0 ? (
        <p style={{ color: P.muted, textAlign: 'center', padding: '30px', fontSize: '14px' }}>
          No time blocks set.
        </p>
      ) : blocks.map(b => {
        const start = new Date(b.booking_date);
        const end   = new Date(start.getTime() + (b.total_duration || 60) * 60_000);
        const isPast = end < new Date();
        return (
          <div key={b.id} style={{ ...cardStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderLeft: `5px solid ${isPast ? P.border : P.amber}`, opacity: isPast ? 0.55 : 1 }}>
            <div>
              <p style={{ fontWeight: 700, color: P.deep, marginBottom: '2px', fontSize: '14px' }}>
                {start.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
              </p>
              <p style={{ fontSize: '13px', color: P.text }}>
                {start.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}
                {' – '}
                {end.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}
                <span style={{ color: P.muted, marginLeft: '8px' }}>({b.total_duration} min)</span>
              </p>
              {b.service_name && b.service_name !== 'Time Block' && (
                <p style={{ fontSize: '12px', color: P.muted, marginTop: '2px' }}>{b.service_name}</p>
              )}
            </div>
            <Btn size="sm" variant="danger" onClick={() => handleRemove(b.id)}>🗑️</Btn>
          </div>
        );
      })}
    </div>
  );
}
